import {
  UserNotificationType,
  type PopUpNotificationProperties,
} from 'dynamo-daos/build/models/misc/userNotificationProperties';
import type { UserNotificationStatus } from 'dynamo-daos/build/models/misc/userNotificationRecord';
import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import getNotifications from '@cartwhl/common/api/getNotifications';
import getNotificationsCount from '@cartwhl/common/api/getNotificationsCount';
import putNotificationStatus from '@cartwhl/common/api/putNotificationStatus';
import * as authStore from '@cartwhl/common/stores/authStore';
import type { UserNotification } from '@cartwhl/common/types';
import {
  type NotificationSurface,
  parseLocation,
  popUpLookupKeys,
} from '@cartwhl/common/utils/notificationLocation';

type PopUpsByLocation = Record<string, UserNotification[]>;

type NotificationStoreState = {
  newNotificationCount: number;
  newPopUpsByLocation: PopUpsByLocation;
};

export const state = proxy<NotificationStoreState>({
  newNotificationCount: 0,
  newPopUpsByLocation: {},
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Notifications', enabled: true });
}

export async function loadForLoggedInUser() {
  await Promise.all([fetchNewNotificationCount(), fetchNewPopUpNotifications()]);
}

async function fetchNewNotificationCount() {
  if (authStore.state.authStatus !== 'user') {
    state.newNotificationCount = 0;
    return;
  }

  const { data, error } = await getNotificationsCount();

  if (error || data == null) {
    state.newNotificationCount = 0;
    return;
  }

  state.newNotificationCount = data.newCount;
}

/** First queued `new` pop-up for this surface, route, and anchor (e.g. `#user-menu`). */
export function getPopUpForRoute(
  surface: NotificationSurface,
  pathname: string,
  anchor: string,
): UserNotification | undefined {
  const byLocation = state.newPopUpsByLocation;
  for (const locationKey of popUpLookupKeys(surface, pathname, anchor)) {
    const notification = byLocation[locationKey]?.[0];
    if (notification) {
      return notification;
    }
  }
  return undefined;
}

function groupPopUpNotificationsByLocation(
  notifications: UserNotification[],
): PopUpsByLocation {
  const byLocation: PopUpsByLocation = {};

  for (const notification of notifications) {
    const { location } =
      notification.properties as unknown as PopUpNotificationProperties;
    const key = parseLocation(location)?.key ?? location;

    if (!byLocation[key]) {
      byLocation[key] = [];
    }
    byLocation[key].push(notification);
  }

  return byLocation;
}

async function fetchNewPopUpNotifications() {
  if (authStore.state.authStatus !== 'user') {
    state.newPopUpsByLocation = {};
    return;
  }

  const { data, error } = await getNotifications({
    notificationType: UserNotificationType.POP_UP,
    status: 'new',
    limit: 10,
  });

  if (error || !data) {
    return;
  }

  state.newPopUpsByLocation = groupPopUpNotificationsByLocation(data.records);
}

export function removeNewPopUpNotification(notificationID: string) {
  for (const location of Object.keys(state.newPopUpsByLocation)) {
    const list = state.newPopUpsByLocation[location];
    const index = list.findIndex(
      (notification) => notification.notificationID === notificationID,
    );
    if (index >= 0) {
      list.splice(index, 1);
      if (list.length === 0) {
        delete state.newPopUpsByLocation[location];
      }
      return;
    }
  }
}

export async function setNotificationStatus(
  notificationID: string,
  status: UserNotificationStatus,
) {
  const { error } = await putNotificationStatus(notificationID, { status });
  if (error) {
    return false;
  }

  removeNewPopUpNotification(notificationID);
  await fetchNewNotificationCount();
  return true;
}

export function clear() {
  state.newNotificationCount = 0;
  state.newPopUpsByLocation = {};
}
